import { useMemo, useState } from "react";

import { Add, AiSparkles, Upload } from "@similarweb/icons";
import { colorsPalettes } from "@similarweb/styles";
import { Combobox, IconButton } from "@similarweb/ui-components";
import { Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { CSV_TEMPLATE_PATH } from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/consts";
import type {
    CreationMenuItem,
    ICreationMenuItem,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/prompts-report/edit-modal/types";
import { AssetsService } from "app/services/AssetsService";
import { allTrackers } from "app/services/track/track";

interface IPromptCreationDropdown {
    isCreationEnabled: boolean;
    isLoading: boolean;
    canGeneratePrompts: boolean;
    handleManualCreation: VoidFunction;
    handleAutoGeneration: VoidFunction;
    onCSVUploadClick: VoidFunction;
}

export const PromptCreationDropdown = ({
    isCreationEnabled,
    isLoading,
    canGeneratePrompts,
    handleManualCreation,
    handleAutoGeneration,
    onCSVUploadClick,
}: IPromptCreationDropdown) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    const onCSVTemplateDownload = (ev: React.MouseEvent<HTMLAnchorElement>): void => {
        ev.stopPropagation();
        allTrackers.dispatchTrackEvent("Edit Campaign", "click", "add prompts/download csv template");
    };

    const items = useMemo<Record<CreationMenuItem, ICreationMenuItem>>(
        () => ({
            manual: {
                title: translate("llm.prompts.edit.prompts-table.create.option.manual.title"),
                text: translate("llm.prompts.edit.prompts-table.create.option.text"),
                icon: (
                    <Add
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: handleManualCreation,
            },
            generation: {
                title: translate("llm.prompts.edit.prompts-table.create.option.auto.title"),
                text: translate("llm.prompts.edit.prompts-table.create.option.auto.text"),
                isHidden: !canGeneratePrompts,
                icon: (
                    <AiSparkles
                        size="sm"
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: handleAutoGeneration,
            },
            csv: {
                title: translate("llm.prompts.edit.prompts-table.create.option.csv.title"),
                text: (
                    <Flex flexDirection="column">
                        <Text
                            fontVariant="listItem3"
                            color={colorsPalettes.carbon[300]}
                        >
                            {translate("llm.prompts.edit.prompts-table.create.option.csv.text")}
                        </Text>
                        <a
                            onClick={onCSVTemplateDownload}
                            onPointerDown={(ev) => ev.stopPropagation()}
                            href={AssetsService.assetUrl(CSV_TEMPLATE_PATH)}
                            download="prompts_template.csv"
                        >
                            {translate("llm.prompts.edit.prompts-table.create.option.csv.text.link")}
                        </a>
                    </Flex>
                ),
                icon: (
                    <Upload
                        size={20}
                        color={colorsPalettes.carbon[200]}
                    />
                ),
                onClick: onCSVUploadClick,
            },
        }),
        [handleAutoGeneration, handleManualCreation, canGeneratePrompts, onCSVUploadClick],
    );

    const onItemClick = (id: CreationMenuItem): void => {
        setIsOpen(false);
        items[id].onClick();
    };

    return (
        <Combobox
            width="300px"
            placement="bottom-end"
            isOpen={isOpen}
            onOpenChange={(open) => setIsOpen(open && isCreationEnabled)}
            data-automation="prompt-creation-dropdown"
        >
            <Combobox.Trigger>
                <Flex width="100%">
                    <IconButton
                        width="auto"
                        type="outlined"
                        iconName="add"
                        iconSize="sm"
                        isDisabled={!isCreationEnabled}
                        isLoading={isLoading}
                        data-automation="prompt-creation-dropdown-button"
                    >
                        {translate("llm.prompts.edit.prompts-table.create.button")}
                    </IconButton>
                </Flex>
            </Combobox.Trigger>
            <Combobox.Content>
                {Object.entries(items)
                    .filter(([, { isHidden }]) => !isHidden)
                    .map(([id, { title, text, icon }]) => (
                        <Combobox.Option
                            key={id}
                            id={id}
                            textValue={title}
                            onClick={() => onItemClick(id as CreationMenuItem)}
                            data-automation={`prompt-creation-dropdown-item-${id}`}
                        >
                            <Flex
                                gap="6px"
                                padding="5px"
                                minHeight="70px"
                                boxSizing="border-box"
                            >
                                {icon}
                                <Flex
                                    flexDirection="column"
                                    margin="4px 0 0"
                                    gap="6px"
                                >
                                    <Text
                                        fontVariant="input2"
                                        color={colorsPalettes.carbon[500]}
                                    >
                                        {title}
                                    </Text>
                                    <Text
                                        fontVariant="listItem3"
                                        color={colorsPalettes.carbon[300]}
                                    >
                                        {text}
                                    </Text>
                                </Flex>
                            </Flex>
                        </Combobox.Option>
                    ))}
            </Combobox.Content>
        </Combobox>
    );
};
